import React, { useEffect, useState } from 'react';
import { Container, Navbar, Nav, Button } from 'react-bootstrap';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { auth } from '../../services/firebase';
import { onIdTokenChanged, signOut } from 'firebase/auth';
import { connectMobileAuthBridge } from '../../services/mobileAuthBridge';
import '../../styles/layout.css';
import IosFooterPromo from './IosFooterPromo';
import { supportMailto } from '../../config/contact';
import { isEmbeddedOdysseyApp } from '../../services/appEnvironment';
import { useTripStore } from '../../hooks/useTripStore';
import { clearPlanningDraft } from '../../services/planningDraft';

const clearLocalPlanning = () => {
  clearPlanningDraft();
  useTripStore.getState().clearConversation();
};

const Layout = ({ children }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isMobileMode = searchParams.get('mode') === 'mobile' || isEmbeddedOdysseyApp();
  const isLandingPage = location.pathname === '/';
  const [scrolled, setScrolled] = useState(false);
  const [navigationExpanded, setNavigationExpanded] = useState(false);
  const closeNavigation = () => setNavigationExpanded(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLogout = async () => {
    closeNavigation();
    clearLocalPlanning();
    await auth.signOut();
    localStorage.removeItem('token');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('authTokenExpiry');
    navigate('/login');
  };

  useEffect(
    () =>
      connectMobileAuthBridge({
        host: window,
        auth,
        subscribe: onIdTokenChanged,
        signOut,
        clearLocalPlanning,
      }),
    []
  );

  const initials = currentUser?.email?.[0]?.toUpperCase() || 'U';

  return (
    <>
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>

      {!isMobileMode && (
        <Navbar
          expand="lg"
          expanded={navigationExpanded}
          onToggle={setNavigationExpanded}
          fixed="top"
          variant="light"
          className={`app-navbar ${scrolled ? 'is-scrolled' : ''}`}
        >
          <Container>
            <Navbar.Brand
              as={NavLink}
              to="/"
              className="navbar-brand-os"
              onClick={closeNavigation}
            >
              <span className="brand-word">OdysseyAI</span>
            </Navbar.Brand>
            <Navbar.Toggle
              aria-controls="basic-navbar-nav"
              aria-expanded={navigationExpanded}
            />
            <Navbar.Collapse id="basic-navbar-nav">
              <Nav className="me-auto nav-main">
                <Nav.Link as={NavLink} to="/" end onClick={closeNavigation}>
                  Plan a trip
                </Nav.Link>
                <Nav.Link
                  as={NavLink}
                  to="/trip-ideas"
                  onClick={closeNavigation}
                >
                  Trip ideas
                </Nav.Link>
                {currentUser && (
                  <Nav.Link as={NavLink} to="/trips" onClick={closeNavigation}>
                    My trips
                  </Nav.Link>
                )}
                <Nav.Link as={NavLink} to="/upgrade" onClick={closeNavigation}>
                  Pricing
                </Nav.Link>
                <Nav.Link as={NavLink} to="/about" onClick={closeNavigation}>
                  About
                </Nav.Link>
              </Nav>
              <Nav className="align-items-center nav-side">
                {currentUser ? (
                  <>
                    <span className="avatar" aria-hidden>
                      {initials}
                    </span>
                    <Button
                      className="btn-os btn-os--secondary btn-os--sm"
                      onClick={handleLogout}
                    >
                      Log out
                    </Button>
                  </>
                ) : (
                  <>
                    <Nav.Link
                      as={NavLink}
                      to="/login"
                      className="login-link"
                      onClick={closeNavigation}
                    >
                      Log in
                    </Nav.Link>
                    <Button
                      className="btn-os btn-os--primary btn-os--sm"
                      onClick={() => {
                        closeNavigation();
                        navigate('/chat');
                      }}
                    >
                      Start planning
                    </Button>
                  </>
                )}
              </Nav>
            </Navbar.Collapse>
          </Container>
        </Navbar>
      )}

      <Container
        fluid
        className={
          isMobileMode
            ? 'app-shell app-shell--mobile'
            : `app-shell ${isLandingPage ? 'app-shell--landing' : 'app-shell--standard'}`
        }
      >
        <main
          id="main-content"
          className={
            isLandingPage
              ? 'app-main app-main--landing'
              : 'app-main app-main--standard'
          }
        >
          {children}
        </main>
        {!isMobileMode && (
          <footer className="app-footer" role="contentinfo">
            <IosFooterPromo />
            <div className="app-footer__inner">
              <div className="app-footer__brand-block">
                <div className="app-footer__brand">
                  <span className="brand-word">OdysseyAI</span>
                </div>
                <p className="app-footer__tagline">
                  Practical AI-assisted itineraries with real-world context.
                </p>
              </div>

              <nav className="app-footer__col" aria-label="Product">
                <h4>Product</h4>
                <NavLink to="/">Plan a trip</NavLink>
                <NavLink to="/trip-ideas">Trip ideas</NavLink>
                <NavLink to="/upgrade">Pricing</NavLink>
                <NavLink to="/about">About</NavLink>
              </nav>

              <nav className="app-footer__col" aria-label="Trust">
                <h4>Trust</h4>
                <NavLink to="/legal/privacy">Privacy</NavLink>
                <NavLink to="/legal/terms">Terms</NavLink>
                <NavLink to="/legal/refund">Refunds</NavLink>
                <NavLink to="/legal/affiliate">Affiliate disclosure</NavLink>
                <NavLink to="/legal/disclaimer">AI disclaimer</NavLink>
              </nav>

              <nav className="app-footer__col" aria-label="Support">
                <h4>Support</h4>
                <a href={supportMailto()}>Email support</a>
                <NavLink to="/upgrade#faq">Planning credit FAQ</NavLink>
              </nav>
            </div>

            <div className="app-footer__bottom">
              <span>© 2026 OdysseyAI. All rights reserved.</span>
              <span className="app-footer__legal">
                Built with curious travelers in mind.
              </span>
            </div>
          </footer>
        )}
      </Container>
    </>
  );
};

export default Layout;
